import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import GetData from "../../../services/getData";
import logPatientChange from "../../../services/patientLogs";
import { buildPatientLogText } from "../../../services/patientLogMessages";
import { Button, IconButton, SegmentedControl } from "../../../components/ui";
import "./PatientAnketa.css";

/* ------------------------------------------------------------------ */
/*  Помощники для двуязычных строк (ru / en)                           */
/* ------------------------------------------------------------------ */

export const L = (ru, en) => ({ ru, en });

// Вернуть строку для текущего языка; fallback на ru, затем en.
export const pick = (obj, lang) => {
  if (obj == null) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.ru || obj.en || "";
};

// Опция checkbox / radio с явным ключом и двуязычной подписью.
export const op = (key, ru, en) => ({ key, label: L(ru, en) });

/* ------------------------------------------------------------------ */
/*  Схема анкеты (21 раздел). Данные пока никуда не сохраняются.       */
/* ------------------------------------------------------------------ */

export const ANKETA_SECTIONS = [
  {
    id: "personal",
    num: 1,
    title: L("Личные и контактные данные", "Personal and contact information"),
    fields: [
      { id: "fio", kind: "text", label: L("Фамилия имя отчество", "Full name") },
      { id: "birth", kind: "text", label: L("Дата рождения и возраст", "Date of birth and age") },
      {
        id: "sex",
        kind: "checks",
        label: L("Пол при рождении", "Sex at birth"),
        options: [op("f", "Женский", "Female"), op("m", "Мужской", "Male"), op("i", "Интерсекс", "Intersex")],
      },
      { id: "gender_id", kind: "text", label: L("Гендерная идентичность (при желании)", "Gender identity (optional)") },
      {
        id: "height_weight",
        kind: "pairs",
        label: L("Рост и вес", "Height and weight"),
        items: [
          { key: "height", label: L("Рост", "Height"), unit: L("см", "cm") },
          { key: "weight", label: L("Вес", "Weight"), unit: L("кг", "kg") },
        ],
      },
      { id: "phone", kind: "text", label: L("Телефон", "Phone") },
      { id: "email", kind: "text", label: L("Электронная почта", "Email") },
      { id: "address", kind: "text", label: L("Адрес проживания, страна", "Address, country") },
      { id: "citizenship", kind: "text", label: L("Гражданство", "Citizenship") },
      { id: "language", kind: "text", label: L("Основной язык", "Primary language") },
      { id: "profession", kind: "text", label: L("Профессия", "Profession") },
      { id: "workplace", kind: "text", label: L("Место работы", "Workplace") },
      { id: "marital", kind: "text", label: L("Семейное положение", "Marital status") },
      {
        id: "emergency",
        kind: "pairs",
        label: L("Контакт на случай экстренной ситуации", "Emergency contact"),
        items: [
          { key: "name", label: L("Имя", "Name") },
          { key: "phone", label: L("Телефон", "Phone") },
        ],
      },
      {
        id: "insurance",
        kind: "pairs",
        label: L("Страховка", "Insurance"),
        items: [
          { key: "company", label: L("Страховая компания", "Insurance company") },
          { key: "policy", label: L("Номер полиса", "Policy number") },
        ],
      },
      {
        id: "referral",
        kind: "checks",
        label: L("Кто направил", "Referred by"),
        options: [
          op("self", "Самостоятельно", "Self-referred"),
          op("doctor", "Врач", "Doctor"),
          op("insurance", "Страховая", "Insurance"),
          op("relative", "Родственник или знакомый", "Relative or acquaintance"),
          op("other", "Другое", "Other"),
        ],
      },
      { id: "referral_other", kind: "text", label: L("Другое (уточните)", "Other (please specify)") },
      { id: "fill_date", kind: "text", label: L("Дата заполнения", "Date completed") },
    ],
  },
  {
    id: "reason",
    num: 2,
    title: L("Причина обращения и ожидания", "Reason for visit and expectations"),
    fields: [
      { id: "chief_complaints", kind: "textarea", label: L("Главные жалобы сейчас", "Chief complaints now"), rows: 3 },
      {
        id: "onset_course",
        kind: "textarea",
        label: L("Когда они начались и как менялись со временем", "When they started and how they changed over time"),
        rows: 3,
      },
      { id: "severity", kind: "scale", label: L("Выраженность симптомов (0–10)", "Symptom severity (0–10)"), max: 10 },
      { id: "aggravates", kind: "text", label: L("Что усиливает", "What makes it worse") },
      { id: "relieves", kind: "text", label: L("Что облегчает", "What relieves it") },
      {
        id: "prior_eval",
        kind: "textarea",
        label: L("Какие обследования или лечение уже проводились и с каким эффектом", "Prior examinations or treatment and their effect"),
        rows: 3,
      },
      {
        id: "goals",
        kind: "textarea",
        label: L("Ваши главные цели консультации и желаемый результат", "Your main goals for the consultation and desired outcome"),
        rows: 3,
      },
    ],
  },
  {
    id: "urgent",
    num: 3,
    title: L("Текущее состояние и срочные симптомы", "Current state and urgent symptoms"),
    fields: [
      {
        id: "urgent_symptoms",
        kind: "checks",
        label: L("За последние 2 недели отмечались", "In the last 2 weeks you experienced"),
        options: [
          op("chest_pain", "Боль или давление в груди", "Chest pain or pressure"),
          op("dyspnea", "Одышка в покое", "Shortness of breath at rest"),
          op("fainting", "Обморок", "Fainting"),
          op("palpitations", "Выраженное сердцебиение", "Noticeable palpitations"),
          op("weakness", "Внезапная слабость или онемение", "Sudden weakness or numbness"),
          op("speech", "Нарушение речи или зрения", "Speech or vision changes"),
          op("headache", "Сильная новая головная боль", "Severe new headache"),
          op("gi_bleed", "Кровь в рвоте или стуле", "Blood in vomit or stool"),
          op("black_stool", "Черный стул", "Black stool"),
          op("urine_blood", "Кровь в моче", "Blood in urine"),
          op("bleeding", "Необычное кровотечение", "Unusual bleeding"),
          op("fever", "Высокая температура более 3 дней", "High fever for more than 3 days"),
          op("weight_loss", "Резкое снижение веса", "Rapid weight loss"),
          op("night_sweats", "Ночная потливость", "Night sweats"),
          op("allergy", "Тяжелая аллергическая реакция", "Severe allergic reaction"),
          op("selfharm", "Мысли о самоповреждении или суициде", "Thoughts of self-harm or suicide"),
          op("none", "Ничего из перечисленного", "None of the above"),
        ],
      },
      {
        id: "urgent_note",
        kind: "textarea",
        label: L("Если отметили любой симптом, опишите когда он возник и сохраняется ли сейчас", "If you marked any symptom, describe when it started and whether it is still present"),
        rows: 3,
      },
    ],
  },
  {
    id: "hpi",
    num: 4,
    title: L("История настоящего заболевания", "History of present illness"),
    fields: [
      { id: "episode", kind: "text", label: L("Первый эпизод или повторение", "First episode or recurrence") },
      { id: "onset_date", kind: "text", label: L("Точная дата или период начала", "Exact date or period of onset") },
      { id: "localization", kind: "text", label: L("Локализация и характер симптомов", "Location and character of symptoms") },
      { id: "frequency", kind: "text", label: L("Частота и длительность эпизодов", "Frequency and duration of episodes") },
      {
        id: "triggers",
        kind: "text",
        label: L("Связь с едой, нагрузкой, сном, циклом, стрессом", "Association with food, exertion, sleep, cycle, stress"),
      },
      { id: "associated", kind: "text", label: L("Сопутствующие симптомы", "Associated symptoms") },
      {
        id: "dynamics",
        kind: "radio",
        label: L("Динамика", "Course"),
        options: [op("improving", "Улучшается", "Improving"), op("stable", "Без изменений", "Stable"), op("worsening", "Ухудшается", "Worsening")],
      },
      { id: "presumed_cause", kind: "text", label: L("Предполагаемая причина (по мнению пациента)", "Presumed cause (in the patient's opinion)") },
      {
        id: "prior_meds",
        kind: "textarea",
        label: L("Ранее назначенные препараты и эффект", "Previously prescribed medications and their effect"),
        rows: 2,
      },
    ],
  },
  {
    id: "chronic",
    num: 5,
    title: L("Перенесенные и хронические заболевания", "Past and chronic diseases"),
    note: L("Отметьте диагнозы и рядом укажите год, лечение и текущее состояние.", "Mark the diagnoses and note the year, treatment, and current status."),
    fields: [
      {
        id: "cv",
        kind: "checks",
        label: L("Сердечно-сосудистые", "Cardiovascular"),
        options: [
          op("htn", "Гипертония", "Hypertension"),
          op("ihd", "Ишемическая болезнь", "Ischemic heart disease"),
          op("mi", "Инфаркт", "Heart attack"),
          op("arrhythmia", "Аритмия", "Arrhythmia"),
          op("stroke", "Инсульт", "Stroke"),
          op("thrombosis", "Тромбоз", "Thrombosis"),
          op("varicose", "Варикоз", "Varicose veins"),
        ],
      },
      {
        id: "endocrine_dx",
        kind: "checks",
        label: L("Эндокринные и обменные", "Endocrine and metabolic"),
        options: [
          op("diabetes", "Диабет", "Diabetes"),
          op("prediabetes", "Предиабет", "Prediabetes"),
          op("thyroid", "Заболевания щитовидной железы", "Thyroid disease"),
          op("adrenal", "Надпочечники", "Adrenal disorders"),
          op("pituitary", "Гипофиз", "Pituitary disorders"),
          op("obesity", "Ожирение", "Obesity"),
          op("gout", "Подагра", "Gout"),
        ],
      },
      {
        id: "resp_dx",
        kind: "checks",
        label: L("Дыхательная система", "Respiratory"),
        options: [
          op("asthma", "Астма", "Asthma"),
          op("copd", "ХОБЛ", "COPD"),
          op("sleep_apnea", "Апноэ сна", "Sleep apnea"),
          op("pneumonia", "Пневмония", "Pneumonia"),
          op("tb", "Туберкулез", "Tuberculosis"),
        ],
      },
      {
        id: "gi_dx",
        kind: "checks",
        label: L("Желудочно-кишечный тракт", "Gastrointestinal"),
        options: [
          op("gerd", "ГЭРБ", "GERD"),
          op("ulcer", "Язва", "Ulcer"),
          op("gastritis", "Гастрит", "Gastritis"),
          op("ibs", "СРК", "IBS"),
          op("ibd", "ВЗК", "IBD"),
          op("celiac", "Целиакия", "Celiac disease"),
          op("polyps", "Полипы", "Polyps"),
        ],
      },
      {
        id: "liver_dx",
        kind: "checks",
        label: L("Печень, желчный, поджелудочная", "Liver, biliary, pancreas"),
        options: [
          op("hepatitis", "Гепатит", "Hepatitis"),
          op("fatty_liver", "Жировая болезнь печени", "Fatty liver disease"),
          op("stones", "Камни", "Gallstones"),
          op("pancreatitis", "Панкреатит", "Pancreatitis"),
        ],
      },
      {
        id: "renal_dx",
        kind: "checks",
        label: L("Почки и мочевыводящие пути", "Kidneys and urinary tract"),
        options: [
          op("stones", "Камни", "Stones"),
          op("uti", "Инфекции", "Infections"),
          op("ckd", "Хроническая болезнь почек", "Chronic kidney disease"),
          op("incontinence", "Недержание", "Incontinence"),
        ],
      },
      {
        id: "neuro_dx",
        kind: "checks",
        label: L("Нервная система", "Nervous system"),
        options: [
          op("migraine", "Мигрень", "Migraine"),
          op("epilepsy", "Эпилепсия", "Epilepsy"),
          op("neuropathy", "Нейропатия", "Neuropathy"),
          op("dementia", "Деменция", "Dementia"),
          op("other", "Другое", "Other"),
        ],
      },
      {
        id: "psych_dx",
        kind: "checks",
        label: L("Психическое здоровье", "Mental health"),
        options: [
          op("anxiety", "Тревога", "Anxiety"),
          op("depression", "Депрессия", "Depression"),
          op("panic", "Панические атаки", "Panic attacks"),
          op("eating", "РПП", "Eating disorder"),
          op("other", "Другое", "Other"),
        ],
      },
      {
        id: "msk_dx",
        kind: "checks",
        label: L("Опорно-двигательная система", "Musculoskeletal"),
        options: [
          op("arthritis", "Артрит", "Arthritis"),
          op("osteoporosis", "Остеопороз", "Osteoporosis"),
          op("fractures", "Переломы", "Fractures"),
          op("back_pain", "Боли в спине или суставах", "Back or joint pain"),
        ],
      },
      {
        id: "autoimmune_dx",
        kind: "checks",
        label: L("Аутоиммунные болезни", "Autoimmune diseases"),
        options: [
          op("ra", "Ревматоидный артрит", "Rheumatoid arthritis"),
          op("sle", "СКВ", "SLE"),
          op("psoriasis", "Псориаз", "Psoriasis"),
          op("thyroiditis", "Тиреоидит", "Thyroiditis"),
          op("other", "Другое", "Other"),
        ],
      },
      {
        id: "oncology",
        kind: "pairs",
        label: L("Онкологические заболевания", "Oncological diseases"),
        items: [
          { key: "type", label: L("Тип", "Type") },
          { key: "year", label: L("Год", "Year") },
          { key: "treatment", label: L("Лечение", "Treatment") },
        ],
      },
      {
        id: "infectious_dx",
        kind: "checks",
        label: L("Инфекционные заболевания", "Infectious diseases"),
        options: [
          op("hiv", "ВИЧ", "HIV"),
          op("hep_b", "Гепатит B", "Hepatitis B"),
          op("hep_c", "Гепатит C", "Hepatitis C"),
          op("tb", "Туберкулез", "Tuberculosis"),
          op("covid", "COVID", "COVID"),
          op("other", "Другое", "Other"),
        ],
      },
      {
        id: "other_dx",
        kind: "textarea",
        label: L("Другие диагнозы, которых нет в списке", "Other diagnoses not listed"),
        rows: 2,
      },
    ],
  },
  {
    id: "hospital",
    num: 6,
    title: L("Госпитализации, операции, травмы и процедуры", "Hospitalizations, surgeries, injuries and procedures"),
    fields: [
      {
        id: "hospitalizations",
        kind: "table",
        label: L("Госпитализации и операции", "Hospitalizations and surgeries"),
        columns: [
          L("Год / возраст", "Year / age"),
          L("Причина, диагноз", "Reason, diagnosis"),
          L("Операция", "Surgery"),
          L("Осложнения", "Complications"),
          L("Результат", "Outcome"),
        ],
        rows: 4,
      },
      {
        id: "transfusion",
        kind: "radio",
        label: L("Переливание крови", "Blood transfusion"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "transfusion_note", kind: "text", label: L("Если да — когда и почему", "If yes — when and why") },
      {
        id: "implants",
        kind: "radio",
        label: L("Импланты, протезы, кардиостимулятор, металлические конструкции", "Implants, prostheses, pacemaker, metal hardware"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "implants_note", kind: "text", label: L("Если да — уточните", "If yes — please specify") },
    ],
  },
  {
    id: "medications",
    num: 7,
    title: L("Лекарства, биодобавки и процедуры", "Medications, supplements and procedures"),
    note: L(
      "Укажите все препараты, включая гормоны, инъекции, контрацепцию, препараты для снижения веса, пептиды, БАДы и средства по требованию.",
      "List all medications, including hormones, injections, contraception, weight-loss drugs, peptides, supplements and as-needed medicines."
    ),
    fields: [
      {
        id: "medications_table",
        kind: "table",
        label: L("Препараты", "Medications"),
        columns: [
          L("Название", "Name"),
          L("Доза и форма", "Dose and form"),
          L("Как часто", "How often"),
          L("Причина и с какого времени", "Reason and since when"),
        ],
        rows: 6,
      },
      {
        id: "adherence",
        kind: "radio",
        label: L("Принимаете препараты строго по схеме", "Do you take medications strictly as prescribed"),
        options: [op("yes", "Да", "Yes"), op("no", "Нет", "No"), op("sometimes", "Иногда пропускаю", "Sometimes I miss doses")],
      },
      {
        id: "adverse",
        kind: "textarea",
        label: L("Нежелательные реакции или препараты, которые пришлось отменить", "Adverse reactions or medications that had to be discontinued"),
        rows: 2,
      },
    ],
  },
  {
    id: "allergies",
    num: 8,
    title: L("Аллергии и непереносимость", "Allergies and intolerances"),
    fields: [
      {
        id: "allergy_meds",
        kind: "radio",
        label: L("Лекарства", "Medications"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "allergy_meds_note", kind: "text", label: L("Препарат и реакция", "Drug and reaction") },
      {
        id: "allergy_food",
        kind: "radio",
        label: L("Пища", "Food"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "allergy_food_note", kind: "text", label: L("Продукт и реакция", "Product and reaction") },
      {
        id: "allergy_latex",
        kind: "radio",
        label: L("Латекс, антисептики, контраст", "Latex, antiseptics, contrast"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "allergy_latex_note", kind: "text", label: L("Уточните", "Specify") },
      {
        id: "allergy_seasonal",
        kind: "radio",
        label: L("Сезонные или бытовые аллергены", "Seasonal or household allergens"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "allergy_seasonal_note", kind: "text", label: L("Уточните", "Specify") },
      {
        id: "anaphylaxis",
        kind: "radio",
        label: L("Была анафилаксия", "History of anaphylaxis"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "anaphylaxis_note", kind: "text", label: L("Причина и дата", "Cause and date") },
    ],
  },
  {
    id: "family",
    num: 9,
    title: L("Семейный анамнез", "Family history"),
    note: L("Отметьте заболевание, родственника и возраст начала.", "Note the disease, the relative, and the age of onset."),
    fields: [
      {
        id: "fam_cardio",
        kind: "text",
        label: L("Сердечный приступ или инсульт до 55 лет (м) и до 65 лет (ж)", "Heart attack or stroke before 55 (m) and 65 (f)"),
      },
      { id: "fam_htn", kind: "text", label: L("Артериальная гипертензия, высокий холестерин", "Hypertension, high cholesterol") },
      { id: "fam_diabetes", kind: "text", label: L("Сахарный диабет, ожирение", "Diabetes, obesity") },
      {
        id: "fam_thyroid",
        kind: "text",
        label: L("Заболевания щитовидной железы и другие эндокринные болезни", "Thyroid and other endocrine diseases"),
      },
      { id: "fam_thrombosis", kind: "text", label: L("Тромбозы, нарушения свертывания", "Thrombosis, clotting disorders") },
      { id: "fam_oncology", kind: "text", label: L("Онкологические заболевания (вид и возраст)", "Oncological diseases (type and age)") },
      { id: "fam_autoimmune", kind: "text", label: L("Аутоиммунные заболевания", "Autoimmune diseases") },
      { id: "fam_osteoporosis", kind: "text", label: L("Остеопороз, перелом шейки бедра", "Osteoporosis, hip fracture") },
      {
        id: "fam_psych",
        kind: "text",
        label: L("Психические или неврологические заболевания", "Psychiatric or neurological diseases"),
      },
      { id: "fam_genetic", kind: "text", label: L("Наследственные заболевания", "Hereditary diseases") },
      {
        id: "fam_consanguinity",
        kind: "radio",
        label: L("Кровное родство родителей", "Parents are blood relatives"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes"), op("unknown", "Неизвестно", "Unknown")],
      },
    ],
  },
  {
    id: "ros",
    num: 10,
    title: L("Обзор симптомов по системам", "Review of systems"),
    note: L("Отметьте то, что беспокоит сейчас или повторялось в последние 6 месяцев.", "Mark what bothers you now or recurred in the last 6 months."),
    fields: [
      {
        id: "ros_general",
        kind: "checks",
        label: L("Общее состояние", "General"),
        options: [
          op("fatigue", "Слабость", "Weakness"),
          op("tiredness", "Утомляемость", "Fatigue"),
          op("fever", "Температура", "Fever"),
          op("chills", "Озноб", "Chills"),
          op("night_sweats", "Ночная потливость", "Night sweats"),
          op("weight_change", "Изменение веса", "Weight change"),
        ],
      },
      {
        id: "ros_skin",
        kind: "checks",
        label: L("Кожа, волосы, ногти", "Skin, hair, nails"),
        options: [
          op("rash", "Сыпь", "Rash"),
          op("itch", "Зуд", "Itching"),
          op("dryness", "Сухость", "Dryness"),
          op("pigmentation", "Пигментация", "Pigmentation"),
          op("hair_loss", "Выпадение волос", "Hair loss"),
          op("acne", "Акне", "Acne"),
          op("nails", "Ломкость ногтей", "Brittle nails"),
        ],
      },
      {
        id: "ros_head",
        kind: "checks",
        label: L("Голова, глаза, уши", "Head, eyes, ears"),
        options: [
          op("headache", "Головная боль", "Headache"),
          op("dizziness", "Головокружение", "Dizziness"),
          op("vision", "Ухудшение зрения", "Vision loss"),
          op("tinnitus", "Шум в ушах", "Tinnitus"),
          op("hearing", "Снижение слуха", "Hearing loss"),
        ],
      },
      {
        id: "ros_ent",
        kind: "checks",
        label: L("ЛОР", "ENT"),
        options: [
          op("congestion", "Заложенность носа", "Nasal congestion"),
          op("snoring", "Храп", "Snoring"),
          op("sore_throat", "Боль в горле", "Sore throat"),
          op("swallowing", "Нарушение глотания", "Swallowing difficulty"),
          op("voice", "Изменение голоса", "Voice change"),
        ],
      },
      {
        id: "ros_cv",
        kind: "checks",
        label: L("Сердце и сосуды", "Heart and vessels"),
        options: [
          op("chest_pain", "Боль в груди", "Chest pain"),
          op("palpitations", "Сердцебиение", "Palpitations"),
          op("irregular", "Перебои", "Irregular beats"),
          op("dyspnea", "Одышка", "Shortness of breath"),
          op("edema", "Отеки", "Edema"),
          op("bp", "Высокое или низкое давление", "High or low blood pressure"),
        ],
      },
      {
        id: "ros_resp",
        kind: "checks",
        label: L("Дыхание", "Respiratory"),
        options: [
          op("cough", "Кашель", "Cough"),
          op("sputum", "Мокрота", "Sputum"),
          op("wheezing", "Свистящее дыхание", "Wheezing"),
          op("dyspnea", "Одышка", "Shortness of breath"),
          op("apnea", "Остановки дыхания во сне", "Pauses in breathing during sleep"),
        ],
      },
      {
        id: "ros_gi",
        kind: "checks",
        label: L("Пищеварение", "Digestive"),
        options: [
          op("heartburn", "Изжога", "Heartburn"),
          op("nausea", "Тошнота", "Nausea"),
          op("pain", "Боль", "Pain"),
          op("bloating", "Вздутие", "Bloating"),
          op("constipation", "Запор", "Constipation"),
          op("diarrhea", "Диарея", "Diarrhea"),
          op("blood", "Кровь", "Blood"),
          op("appetite", "Изменение аппетита", "Appetite change"),
        ],
      },
      {
        id: "ros_urinary",
        kind: "checks",
        label: L("Мочевая система", "Urinary"),
        options: [
          op("frequency", "Учащение", "Frequency"),
          op("pain", "Боль", "Pain"),
          op("nocturia", "Ночное мочеиспускание", "Nocturia"),
          op("incontinence", "Недержание", "Incontinence"),
          op("blood", "Кровь", "Blood"),
          op("infections", "Инфекции", "Infections"),
        ],
      },
      {
        id: "ros_neuro",
        kind: "checks",
        label: L("Нервная система", "Nervous system"),
        options: [
          op("numbness", "Онемение", "Numbness"),
          op("tingling", "Покалывание", "Tingling"),
          op("weakness", "Слабость", "Weakness"),
          op("tremor", "Тремор", "Tremor"),
          op("seizures", "Судороги", "Seizures"),
          op("memory", "Нарушение памяти", "Memory impairment"),
        ],
      },
      {
        id: "ros_msk",
        kind: "checks",
        label: L("Мышцы и суставы", "Muscles and joints"),
        options: [
          op("pain", "Боль", "Pain"),
          op("stiffness", "Скованность", "Stiffness"),
          op("swelling", "Отек", "Swelling"),
          op("cramps", "Судороги", "Cramps"),
          op("weakness", "Слабость", "Weakness"),
          op("limited", "Ограничение движения", "Limited movement"),
        ],
      },
      {
        id: "ros_mood",
        kind: "checks",
        label: L("Настроение", "Mood"),
        options: [
          op("anxiety", "Тревога", "Anxiety"),
          op("low_mood", "Сниженное настроение", "Low mood"),
          op("irritability", "Раздражительность", "Irritability"),
          op("panic", "Панические атаки", "Panic attacks"),
          op("swings", "Перепады настроения", "Mood swings"),
        ],
      },
      {
        id: "ros_sleep",
        kind: "checks",
        label: L("Сон", "Sleep"),
        options: [
          op("insomnia", "Трудно заснуть", "Difficulty falling asleep"),
          op("awakenings", "Пробуждения", "Awakenings"),
          op("early", "Ранний подъем", "Early waking"),
          op("snoring", "Храп", "Snoring"),
          op("daytime", "Дневная сонливость", "Daytime sleepiness"),
        ],
      },
      {
        id: "ros_other",
        kind: "textarea",
        label: L("Другие симптомы и пояснения", "Other symptoms and explanations"),
        rows: 2,
      },
    ],
  },
  {
    id: "endocrine",
    num: 11,
    title: L("Эндокринный и метаболический анамнез", "Endocrine and metabolic history"),
    fields: [
      {
        id: "endocrine_symptoms",
        kind: "checks",
        label: L("Есть ли", "Do you have"),
        options: [
          op("thirst", "Сильная жажда", "Excessive thirst"),
          op("polyuria", "Частое мочеиспускание", "Frequent urination"),
          op("sugar_craving", "Тяга к сладкому", "Sugar cravings"),
          op("weakness", "Приступы слабости или дрожи", "Episodes of weakness or shaking"),
          op("cold", "Непереносимость холода", "Cold intolerance"),
          op("heat", "Непереносимость жары", "Heat intolerance"),
          op("sweating", "Потливость", "Sweating"),
          op("weight_change", "Изменение веса", "Weight change"),
          op("edema", "Отеки", "Edema"),
          op("neck", "Изменения шеи или щитовидной железы", "Neck or thyroid changes"),
          op("hoarseness", "Осиплость", "Hoarseness"),
          op("swallowing", "Трудности при глотании", "Swallowing difficulty"),
          op("bp", "Повышение давления", "High blood pressure"),
          op("stretch", "Растяжки", "Stretch marks"),
          op("bruising", "Синяки", "Bruising"),
          op("muscle_weakness", "Мышечная слабость", "Muscle weakness"),
          op("appearance", "Изменение внешности", "Appearance changes"),
        ],
      },
      {
        id: "diabetes_hx",
        kind: "radio",
        label: L("Диабет или предиабет", "Diabetes or prediabetes"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "glucose", kind: "text", label: L("Максимальная глюкоза или HbA1c", "Maximum glucose or HbA1c") },
      {
        id: "thyroid_hx",
        kind: "radio",
        label: L("Заболевания щитовидной железы или операции", "Thyroid disease or surgery"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "thyroid_note", kind: "text", label: L("Уточните", "Specify") },
      { id: "tsh", kind: "text", label: L("Последние известные ТТГ и Т4 свободный, дата", "Last known TSH and free T4, date") },
    ],
  },
  {
    id: "female",
    num: 12,
    title: L("Женский репродуктивный и гинекологический анамнез", "Female reproductive and gynecological history"),
    note: L("Заполняется пациентками, для которых раздел применим.", "To be completed by patients for whom this section applies."),
    fields: [
      { id: "menarche", kind: "text", label: L("Возраст первой менструации", "Age at first menstruation") },
      { id: "lmp", kind: "text", label: L("Дата первого дня последней менструации", "First day of last menstrual period") },
      {
        id: "cycle",
        kind: "pairs",
        label: L("Цикл", "Cycle"),
        items: [
          { key: "days", label: L("Каждые", "Every"), unit: L("дней", "days") },
          { key: "duration", label: L("Длительность", "Duration"), unit: L("дней", "days") },
        ],
      },
      {
        id: "cycle_regular",
        kind: "radio",
        label: L("Характер цикла", "Cycle pattern"),
        options: [op("regular", "Регулярный", "Regular"), op("irregular", "Нерегулярный", "Irregular")],
      },
      {
        id: "menstruation",
        kind: "checks",
        label: L("Менструации", "Menstruation"),
        options: [
          op("heavy", "Обильные", "Heavy"),
          op("painful", "Болезненные", "Painful"),
          op("spotting", "Межменструальные выделения", "Intermenstrual spotting"),
          op("none", "Нет менструаций", "No periods"),
        ],
      },
      {
        id: "pregnancies",
        kind: "pairs",
        label: L("Беременности и исходы", "Pregnancies and outcomes"),
        items: [
          { key: "preg", label: L("Беременностей", "Pregnancies") },
          { key: "births", label: L("Родов", "Births") },
          { key: "cesarean", label: L("Кесаревых", "Cesarean") },
          { key: "miscarriage", label: L("Выкидышей", "Miscarriages") },
          { key: "abortion", label: L("Абортов", "Abortions") },
        ],
      },
      {
        id: "current_pregnancy",
        kind: "radio",
        label: L("Беременность сейчас", "Currently pregnant"),
        options: [
          op("no", "Нет", "No"),
          op("possible", "Возможна", "Possible"),
          op("yes", "Да", "Yes"),
          op("na", "Не применимо", "Not applicable"),
        ],
      },
      {
        id: "breastfeeding",
        kind: "radio",
        label: L("Грудное вскармливание", "Breastfeeding"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "contraception", kind: "text", label: L("Контрацепция", "Contraception") },
      {
        id: "menopause",
        kind: "radio",
        label: L("Менопауза", "Menopause"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      {
        id: "menopause_details",
        kind: "pairs",
        items: [
          { key: "age", label: L("Возраст", "Age") },
          { key: "symptoms", label: L("Симптомы", "Symptoms") },
        ],
      },
      {
        id: "hrt",
        kind: "radio",
        label: L("Гормональная терапия", "Hormone therapy"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "hrt_note", kind: "text", label: L("Уточните", "Specify") },
      {
        id: "pap",
        kind: "pairs",
        label: L("Последний ПАП-тест", "Last Pap test"),
        items: [
          { key: "date", label: L("Дата", "Date") },
          { key: "result", label: L("Результат", "Result") },
        ],
      },
      {
        id: "mammogram",
        kind: "pairs",
        label: L("Последняя маммография или УЗИ груди", "Last mammogram or breast ultrasound"),
        items: [
          { key: "date", label: L("Дата", "Date") },
          { key: "result", label: L("Результат", "Result") },
        ],
      },
      {
        id: "gyn_dx",
        kind: "textarea",
        label: L("Гинекологические диагнозы или операции", "Gynecological diagnoses or surgeries"),
        rows: 2,
      },
    ],
  },
  {
    id: "male",
    num: 13,
    title: L("Мужской урологический и репродуктивный анамнез", "Male urological and reproductive history"),
    note: L("Заполняется пациентами, для которых раздел применим.", "To be completed by patients for whom this section applies."),
    fields: [
      {
        id: "urination",
        kind: "checks",
        label: L("Мочеиспускание", "Urination"),
        options: [
          op("weak_stream", "Слабая струя", "Weak stream"),
          op("hesitancy", "Затруднение", "Hesitancy"),
          op("nocturia", "Ночные позывы", "Nocturia"),
          op("pain", "Боль", "Pain"),
          op("none", "Без жалоб", "No complaints"),
        ],
      },
      { id: "prostate", kind: "text", label: L("Предстательная железа — диагнозы или операции", "Prostate — diagnoses or surgeries") },
      {
        id: "psa",
        kind: "pairs",
        label: L("Последний ПСА", "Last PSA"),
        items: [
          { key: "date", label: L("Дата", "Date") },
          { key: "result", label: L("Результат", "Result") },
        ],
      },
      {
        id: "sexual_function",
        kind: "checks",
        label: L("Сексуальная функция", "Sexual function"),
        options: [
          op("libido", "Снижение либидо", "Decreased libido"),
          op("ed", "Эректильная дисфункция", "Erectile dysfunction"),
          op("none", "Без жалоб", "No complaints"),
        ],
      },
      {
        id: "fertility",
        kind: "pairs",
        label: L("Фертильность", "Fertility"),
        items: [{ key: "children", label: L("Дети", "Children") }],
      },
      {
        id: "fertility_difficulty",
        kind: "radio",
        label: L("Трудности с зачатием", "Difficulty conceiving"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      {
        id: "testosterone",
        kind: "radio",
        label: L("Тестостерон или другая гормональная терапия", "Testosterone or other hormone therapy"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "testosterone_note", kind: "text", label: L("Уточните", "Specify") },
    ],
  },
  {
    id: "nutrition",
    num: 14,
    title: L("Питание, пищеварение и вес", "Nutrition, digestion and weight"),
    fields: [
      {
        id: "diet_type",
        kind: "checks",
        label: L("Тип питания", "Diet type"),
        options: [
          op("mixed", "Смешанный", "Mixed"),
          op("vegetarian", "Вегетарианский", "Vegetarian"),
          op("vegan", "Веганский", "Vegan"),
          op("lowcarb", "Низкоуглеводный", "Low-carb"),
          op("other", "Другое", "Other"),
        ],
      },
      { id: "diet_other", kind: "text", label: L("Другое (уточните)", "Other (specify)") },
      {
        id: "intake",
        kind: "pairs",
        label: L("Режим питания", "Intake"),
        items: [
          { key: "meals", label: L("Приемов пищи в день", "Meals per day") },
          { key: "water", label: L("Вода", "Water"), unit: L("л в день", "L per day") },
          { key: "coffee", label: L("Кофе или энергетики", "Coffee or energy drinks") },
          { key: "fruits", label: L("Овощи и фрукты", "Fruits and vegetables"), unit: L("порций в день", "servings per day") },
        ],
      },
      {
        id: "protein",
        kind: "radio",
        label: L("Белок", "Protein"),
        options: [op("enough", "Достаточно", "Enough"), op("low", "Мало", "Low"), op("unknown", "Не знаю", "Not sure")],
      },
      {
        id: "diet_restrictions",
        kind: "textarea",
        label: L("Пищевые ограничения, непереносимость, эпизоды переедания или строгих диет", "Food restrictions, intolerances, episodes of overeating or strict diets"),
        rows: 2,
      },
      {
        id: "stool_frequency",
        kind: "text",
        label: L("Стул", "Stool frequency"),
        placeholder: L("раз в день или неделю", "times per day or week"),
      },
      {
        id: "stool_type",
        kind: "radio",
        label: L("Характер стула", "Stool pattern"),
        options: [
          op("normal", "Нормальный", "Normal"),
          op("constipation", "Запор", "Constipation"),
          op("diarrhea", "Диарея", "Diarrhea"),
          op("alternating", "Чередование", "Alternating"),
        ],
      },
      {
        id: "weight_change",
        kind: "radio",
        label: L("Изменение веса за год", "Weight change over the year"),
        options: [op("none", "Нет", "None"), op("gain", "Прибавка", "Gained"), op("loss", "Снижение", "Lost")],
      },
      {
        id: "weight_details",
        kind: "pairs",
        items: [
          { key: "gain_kg", label: L("Прибавка", "Gained"), unit: L("кг", "kg") },
          { key: "loss_kg", label: L("Снижение", "Lost"), unit: L("кг", "kg") },
        ],
      },
      {
        id: "weight_intent",
        kind: "radio",
        label: L("Характер изменения", "Nature of change"),
        options: [op("intentional", "Намеренное", "Intentional"), op("unintentional", "Непреднамеренное", "Unintentional")],
      },
      {
        id: "typical_diet",
        kind: "textarea",
        label: L("Пример обычного рациона за день", "Example of a typical daily diet"),
        rows: 3,
      },
    ],
  },
  {
    id: "lifestyle",
    num: 15,
    title: L("Образ жизни и социальный анамнез", "Lifestyle and social history"),
    fields: [
      {
        id: "activity",
        kind: "pairs",
        label: L("Физическая активность", "Physical activity"),
        items: [
          { key: "type", label: L("Вид", "Type") },
          { key: "days", label: L("Дней в неделю", "Days per week") },
          { key: "minutes", label: L("Минут в неделю", "Minutes per week") },
        ],
      },
      { id: "sedentary", kind: "text", label: L("Сидячее время", "Sedentary time"), placeholder: L("часов в день", "hours per day") },
      {
        id: "sleep",
        kind: "pairs",
        label: L("Сон", "Sleep"),
        items: [
          { key: "bedtime", label: L("Ложусь", "Bedtime") },
          { key: "waketime", label: L("Встаю", "Wake time") },
          { key: "hours", label: L("Сон", "Sleep"), unit: L("часов", "hours") },
        ],
      },
      { id: "sleep_quality", kind: "scale", label: L("Качество сна (0–10)", "Sleep quality (0–10)"), max: 10 },
      {
        id: "tobacco",
        kind: "radio",
        label: L("Табак и никотин", "Tobacco and nicotine"),
        options: [op("never", "Никогда", "Never"), op("current", "Сейчас", "Current"), op("former", "Раньше", "Former")],
      },
      {
        id: "tobacco_details",
        kind: "pairs",
        items: [
          { key: "type", label: L("Вид", "Type") },
          { key: "amount", label: L("Количество", "Amount") },
          { key: "duration", label: L("Стаж", "Duration") },
        ],
      },
      {
        id: "alcohol",
        kind: "radio",
        label: L("Алкоголь", "Alcohol"),
        options: [op("none", "Нет", "None"), op("rare", "Редко", "Rarely"), op("weekly", "Еженедельно", "Weekly")],
      },
      { id: "alcohol_weekly", kind: "text", label: L("Порций в неделю", "Drinks per week") },
      {
        id: "recreational",
        kind: "radio",
        label: L("Рекреационные вещества", "Recreational substances"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "recreational_note", kind: "text", label: L("Вид и частота", "Type and frequency") },
      { id: "stress_level", kind: "scale", label: L("Уровень стресса (0–10)", "Stress level (0–10)"), max: 10 },
      { id: "stress_sources", kind: "text", label: L("Основные источники стресса", "Main sources of stress") },
      {
        id: "home_safety",
        kind: "radio",
        label: L("Поддержка и безопасность дома", "Support and safety at home"),
        options: [
          op("safe", "Чувствую себя в безопасности", "I feel safe"),
          op("talk", "Нужна конфиденциальная беседа", "I need a confidential talk"),
        ],
      },
      {
        id: "work_schedule",
        kind: "radio",
        label: L("Рабочий график", "Work schedule"),
        options: [op("day", "Дневной", "Day"), op("night", "Ночной", "Night"), op("shift", "Сменный", "Shift")],
      },
      { id: "work_hours", kind: "text", label: L("Часов в неделю", "Hours per week") },
      { id: "travel", kind: "text", label: L("Путешествия за 12 месяцев (страны и даты)", "Travel in the last 12 months (countries and dates)") },
      {
        id: "exposures",
        kind: "checks",
        label: L("Воздействия на работе или дома", "Occupational or home exposures"),
        options: [
          op("chemicals", "Химикаты", "Chemicals"),
          op("dust", "Пыль", "Dust"),
          op("mold", "Плесень", "Mold"),
          op("radiation", "Радиация", "Radiation"),
          op("metals", "Тяжелые металлы", "Heavy metals"),
          op("none", "Нет", "None"),
        ],
      },
    ],
  },
  {
    id: "psych",
    num: 16,
    title: L("Психоэмоциональное состояние", "Psycho-emotional state"),
    note: L(
      "За последние 2 недели как часто беспокоили следующие состояния: 0 — никогда, 1 — несколько дней, 2 — более половины дней, 3 — почти ежедневно.",
      "Over the last 2 weeks, how often were you bothered by the following: 0 — not at all, 1 — several days, 2 — more than half the days, 3 — nearly every day."
    ),
    fields: [
      { id: "phq_interest", kind: "scale", label: L("Сниженный интерес или удовольствие", "Little interest or pleasure"), max: 3 },
      { id: "phq_depressed", kind: "scale", label: L("Подавленность или безнадежность", "Feeling down or hopeless"), max: 3 },
      { id: "phq_anxiety", kind: "scale", label: L("Тревога или напряжение", "Anxiety or tension"), max: 3 },
      {
        id: "phq_worry",
        kind: "scale",
        label: L("Невозможность остановить тревожные мысли", "Unable to stop worrying thoughts"),
        max: 3,
      },
      {
        id: "psych_prior",
        kind: "radio",
        label: L("Ранее обращались к психологу или психиатру", "Previously saw a psychologist or psychiatrist"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "psych_prior_note", kind: "text", label: L("Уточните", "Specify") },
      {
        id: "self_harm",
        kind: "radio",
        label: L("Мысли о самоповреждении или суициде сейчас", "Current thoughts of self-harm or suicide"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
    ],
  },
  {
    id: "infections",
    num: 17,
    title: L("Инфекции, вакцинация и эпидемиологический анамнез", "Infections, vaccination and epidemiological history"),
    fields: [
      {
        id: "vaccination",
        kind: "checks",
        label: L("Вакцинация", "Vaccination"),
        options: [
          op("covid", "COVID", "COVID"),
          op("flu", "Грипп", "Flu"),
          op("tetanus", "Столбняк или дифтерия", "Tetanus or diphtheria"),
          op("hep_b", "Гепатит B", "Hepatitis B"),
          op("hpv", "ВПЧ", "HPV"),
          op("pneumo", "Пневмококк", "Pneumococcus"),
          op("other", "Другое", "Other"),
        ],
      },
      { id: "tetanus_date", kind: "text", label: L("Дата последней вакцинации от столбняка", "Date of last tetanus vaccination") },
      {
        id: "vaccine_reaction",
        kind: "radio",
        label: L("Реакции на вакцины", "Reactions to vaccines"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "vaccine_reaction_note", kind: "text", label: L("Уточните", "Specify") },
      {
        id: "infection_contact",
        kind: "radio",
        label: L("Контакт с туберкулезом, гепатитом, ВИЧ или другими значимыми инфекциями", "Contact with TB, hepatitis, HIV or other significant infections"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "infection_contact_note", kind: "text", label: L("Уточните", "Specify") },
      {
        id: "frequent_infections",
        kind: "radio",
        label: L("Частые или необычные инфекции", "Frequent or unusual infections"),
        options: [op("no", "Нет", "No"), op("yes", "Да", "Yes")],
      },
      { id: "frequent_infections_note", kind: "text", label: L("Уточните", "Specify") },
    ],
  },
  {
    id: "screenings",
    num: 18,
    title: L("Профилактические обследования", "Preventive screenings"),
    fields: [
      { id: "exam_general", kind: "text", label: L("Общий медицинский осмотр (дата и результат)", "General check-up (date and result)") },
      {
        id: "blood_tests",
        kind: "pairs",
        label: L("Анализы крови", "Blood tests"),
        items: [
          { key: "date", label: L("Дата", "Date") },
          { key: "where", label: L("Где выполнены", "Where done") },
        ],
      },
      { id: "bp", kind: "text", label: L("Артериальное давление (последнее значение)", "Blood pressure (last value)") },
      { id: "lipid", kind: "text", label: L("Липидограмма (дата и результат)", "Lipid profile (date and result)") },
      { id: "diabetes_screen", kind: "text", label: L("Скрининг диабета (дата и результат)", "Diabetes screening (date and result)") },
      { id: "colonoscopy", kind: "text", label: L("Колоноскопия или анализ кала (дата и результат)", "Colonoscopy or stool test (date and result)") },
      { id: "densitometry", kind: "text", label: L("Денситометрия (дата и результат)", "Densitometry (date and result)") },
      { id: "skin_check", kind: "text", label: L("Осмотр кожи и родинок (дата и результат)", "Skin and mole check (date and result)") },
      {
        id: "dental_ophthal",
        kind: "text",
        label: L("Осмотр стоматолога и офтальмолога (дата и результат)", "Dental and eye exam (date and result)"),
      },
    ],
  },
  {
    id: "measurements",
    num: 19,
    title: L("Измерения на приеме (заполняет медицинский сотрудник)", "Measurements at the visit (completed by medical staff)"),
    fields: [
      { id: "measure_date", kind: "text", label: L("Дата и время", "Date and time") },
      { id: "height", kind: "text", label: L("Рост", "Height"), placeholder: L("см", "cm") },
      { id: "weight", kind: "text", label: L("Вес", "Weight"), placeholder: L("кг", "kg") },
      { id: "bmi", kind: "text", label: L("Индекс массы тела", "Body mass index"), placeholder: L("кг/м²", "kg/m²") },
      { id: "waist", kind: "text", label: L("Окружность талии", "Waist circumference"), placeholder: L("см", "cm") },
      { id: "temperature", kind: "text", label: L("Температура", "Temperature"), placeholder: L("°C", "°C") },
      {
        id: "bp",
        kind: "pairs",
        label: L("Артериальное давление", "Blood pressure"),
        items: [
          { key: "right", label: L("Правая рука", "Right arm"), unit: L("мм рт. ст.", "mmHg") },
          { key: "left", label: L("Левая рука", "Left arm"), unit: L("мм рт. ст.", "mmHg") },
        ],
      },
      { id: "pulse", kind: "text", label: L("Пульс", "Pulse"), placeholder: L("в минуту", "per minute") },
      {
        id: "pulse_regular",
        kind: "radio",
        label: L("Ритм", "Rhythm"),
        options: [op("regular", "Регулярный", "Regular"), op("irregular", "Нерегулярный", "Irregular")],
      },
      { id: "respiratory_rate", kind: "text", label: L("Частота дыхания", "Respiratory rate"), placeholder: L("в минуту", "per minute") },
      { id: "saturation", kind: "text", label: L("Сатурация", "Oxygen saturation"), placeholder: L("%", "%") },
      { id: "pain", kind: "scale", label: L("Боль (0–10)", "Pain (0–10)"), max: 10 },
      { id: "pain_location", kind: "text", label: L("Локализация боли", "Pain location") },
    ],
  },
  {
    id: "documents",
    num: 20,
    title: L("Документы и данные для консультации", "Documents and data for the consultation"),
    fields: [
      {
        id: "attached_docs",
        kind: "checks",
        label: L("Приложено", "Attached"),
        options: [
          op("tests", "Результаты анализов", "Test results"),
          op("reports", "Заключения врачей", "Doctor's reports"),
          op("discharge", "Выписки", "Discharge summaries"),
          op("ultrasound", "УЗИ", "Ultrasound"),
          op("imaging", "КТ или МРТ", "CT or MRI"),
          op("med_list", "Список лекарств", "Medication list"),
        ],
      },
      {
        id: "devices",
        kind: "checks",
        label: L("Используемые устройства", "Devices used"),
        options: [
          op("glucometer", "Глюкометр", "Glucometer"),
          op("tonometer", "Тонометр", "Blood pressure monitor"),
          op("smartwatch", "Умные часы", "Smartwatch"),
          op("sleep_tracker", "Трекер сна", "Sleep tracker"),
          op("cgm", "CGM", "CGM"),
          op("other", "Другое", "Other"),
        ],
      },
      { id: "devices_other", kind: "text", label: L("Другое (уточните)", "Other (specify)") },
      {
        id: "discuss_topics",
        kind: "textarea",
        label: L("Какие данные или вопросы обязательно обсудить с врачом", "What data or questions must be discussed with the doctor"),
        rows: 3,
      },
    ],
  },
  {
    id: "confirmation",
    num: 21,
    title: L("Подтверждение пациента", "Patient confirmation"),
    fields: [
      {
        id: "confirm_note",
        kind: "note",
        text: L(
          "Я подтверждаю, что предоставленная информация является полной и достоверной насколько мне известно. Я понимаю, что анкета не заменяет консультацию врача, осмотр и экстренную медицинскую помощь. Я обязуюсь сообщать врачу об изменениях состояния, новых препаратах, беременности и аллергических реакциях.",
          "I confirm that the information provided is complete and accurate to the best of my knowledge. I understand that this questionnaire does not replace a doctor's consultation, examination, or emergency medical care. I agree to inform the doctor of any changes in my condition, new medications, pregnancy, and allergic reactions."
        ),
      },
      { id: "patient_name", kind: "text", label: L("Фамилия и имя пациента", "Patient full name") },
      {
        id: "patient_signature",
        kind: "text",
        label: L("Подпись пациента или законного представителя", "Signature of patient or legal representative"),
      },
      { id: "signature_date", kind: "text", label: L("Дата", "Date") },
      {
        id: "staff_name",
        kind: "text",
        label: L("Фамилия и имя медицинского сотрудника, проверившего анкету", "Full name of the medical staff member who reviewed the questionnaire"),
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Поля-рендереры                                                     */
/* ------------------------------------------------------------------ */

export function FieldLabel({ label, lang }) {
  if (!label) return null;
  return <label className="anketa-label">{pick(label, lang)}</label>;
}

/* Прочерк для незаполненных полей в режиме просмотра. */
export const ANKETA_EMPTY = "—";

// Подписи выбранных вариантов (чекбоксы / радио) для режима просмотра.
function selectedOptionsText(options, selected, lang) {
  const keys = Array.isArray(selected) ? selected : selected ? [selected] : [];
  return options
    .filter((opt) => keys.includes(opt.key))
    .map((opt) => pick(opt.label, lang))
    .join(", ");
}

// Статичное значение поля в режиме просмотра.
export function ReadOnlyValue({ value, multiline = false }) {
  const text = value === 0 ? "0" : value;
  const isEmpty = text == null || String(text).trim() === "";
  return (
    <div
      className={
        "anketa-value" +
        (multiline ? " anketa-value--multiline" : "") +
        (isEmpty ? " is-empty" : " is-filled")
      }
    >
      {isEmpty ? ANKETA_EMPTY : text}
    </div>
  );
}

/**
 * Строка документа в режиме просмотра: «Подпись:  значение на линии».
 * Повторяет вид анамнеза в режиме чтения.
 */
export function DocField({ label, lang, children, stack = false }) {
  const text = label ? pick(label, lang) : "";
  const withColon = text && !text.trim().endsWith("?");
  return (
    <div className={`anketa-field anketa-field--doc${stack ? " anketa-field--doc-stack" : ""}`}>
      {text ? (
        <span className="anketa-label anketa-label--doc">
          {text}
          {withColon ? ":" : ""}
        </span>
      ) : null}
      {children}
    </div>
  );
}

/**
 * Рендер поля анкеты.
 * readOnly = true — режим просмотра (только текст), false — редактирование.
 */
export function renderField(field, lang, values, handlers, readOnly = false) {
  const value = values[field.id];

  switch (field.kind) {
    case "sub":
      return <div className="anketa-sub">{pick(field.title, lang)}</div>;

    case "note":
      return <div className="anketa-note">{pick(field.text, lang)}</div>;

    case "text":
      if (readOnly) {
        return (
          <DocField label={field.label} lang={lang}>
            <ReadOnlyValue value={value} />
          </DocField>
        );
      }
      return (
        <div className="anketa-field">
          <FieldLabel label={field.label} lang={lang} />
          <input
            className="anketa-input"
            type="text"
            value={value || ""}
            onChange={(e) => handlers.setVal(field.id, e.target.value)}
            placeholder={field.placeholder ? pick(field.placeholder, lang) : ""}
          />
        </div>
      );

    case "textarea":
      if (readOnly) {
        return (
          <DocField label={field.label} lang={lang} stack>
            <ReadOnlyValue value={value} multiline />
          </DocField>
        );
      }
      return (
        <div className="anketa-field">
          <FieldLabel label={field.label} lang={lang} />
          <textarea
            className="anketa-textarea"
            rows={field.rows || 3}
            value={value || ""}
            onChange={(e) => handlers.setVal(field.id, e.target.value)}
          />
        </div>
      );

    case "checks": {
      const selected = Array.isArray(value) ? value : [];
      if (readOnly) {
        return (
          <DocField label={field.label} lang={lang}>
            <ReadOnlyValue
              value={selectedOptionsText(field.options, selected, lang)}
            />
          </DocField>
        );
      }
      return (
        <div className="anketa-field">
          <FieldLabel label={field.label} lang={lang} />
          <div className="anketa-options">
            {field.options.map((opt) => {
              const checked = selected.includes(opt.key);
              return (
                <label className="anketa-option" key={opt.key}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handlers.toggleCheck(field.id, opt.key)}
                  />
                  <span className="anketa-option-text">{pick(opt.label, lang)}</span>
                </label>
              );
            })}
          </div>
        </div>
      );
    }

    case "radio": {
      const selected = value || "";
      if (readOnly) {
        return (
          <DocField label={field.label} lang={lang}>
            <ReadOnlyValue
              value={selectedOptionsText(field.options, selected, lang)}
            />
          </DocField>
        );
      }
      return (
        <div className="anketa-field">
          <FieldLabel label={field.label} lang={lang} />
          <div className="anketa-options">
            {field.options.map((opt) => (
              <label className="anketa-option" key={opt.key}>
                <input
                  type="radio"
                  name={field.id}
                  checked={selected === opt.key}
                  onChange={() => handlers.setVal(field.id, opt.key)}
                />
                <span className="anketa-option-text">{pick(opt.label, lang)}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    case "scale": {
      const selected = value == null || value === "" ? null : Number(value);
      const max = field.max ?? 10;
      if (readOnly) {
        return (
          <DocField label={field.label} lang={lang}>
            <ReadOnlyValue value={value} />
          </DocField>
        );
      }
      const options = Array.from({ length: max + 1 }, (_, n) => n);
      return (
        <div className="anketa-field">
          <FieldLabel label={field.label} lang={lang} />
          <div className="anketa-scale">
            {options.map((n) => (
              <button
                type="button"
                key={n}
                className={`anketa-scale-option ${selected === n ? "active" : ""}`}
                onClick={() => handlers.setVal(field.id, String(n))}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      );
    }

    case "table": {
      const rows = Array.isArray(value) ? value : [];
      const count = field.rows || 3;
      if (readOnly) {
        // Показываем только заполненные строки.
        const filled = rows.filter(
          (row) =>
            row &&
            field.columns.some(
              (col, i) => String((row[i] ?? "")).trim() !== "",
            ),
        );
        if (filled.length === 0) {
          return (
            <DocField label={field.label} lang={lang}>
              <ReadOnlyValue value="" />
            </DocField>
          );
        }
        return (
          <DocField label={field.label} lang={lang} stack>
            <div className="anketa-table-wrap anketa-table-wrap--doc">
              <table className="anketa-table anketa-table--readonly">
                <thead>
                  <tr>
                    {field.columns.map((col, i) => (
                      <th key={i}>{pick(col, lang)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filled.map((row, r) => (
                    <tr key={r}>
                      {field.columns.map((col, i) => (
                        <td key={i}>{(row && row[i]) || ANKETA_EMPTY}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DocField>
        );
      }
      return (
        <div className="anketa-field anketa-field--table">
          <FieldLabel label={field.label} lang={lang} />
          <div className="anketa-table-wrap">
            <table className="anketa-table">
              <thead>
                <tr>
                  {field.columns.map((col, i) => (
                    <th key={i}>{pick(col, lang)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: count }).map((_, r) => (
                  <tr key={r}>
                    {field.columns.map((col, i) => (
                      <td key={i}>
                        <input
                          type="text"
                          value={(rows[r] && rows[r][i]) || ""}
                          onChange={(e) => handlers.setCell(field.id, r, i, e.target.value)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    case "pairs": {
      const obj = value && typeof value === "object" && !Array.isArray(value) ? value : {};
      if (readOnly) {
        return (
          <DocField label={field.label} lang={lang} stack>
            <div className="anketa-pairs anketa-pairs--doc">
              {field.items.map((item) => {
                const text = obj[item.key];
                const shown =
                  text === 0 || (text != null && String(text).trim() !== "")
                    ? text
                    : "";
                return (
                  <div className="anketa-field anketa-field--doc" key={item.key}>
                    <span className="anketa-label anketa-label--doc">
                      {pick(item.label, lang)}:
                    </span>
                    <ReadOnlyValue value={shown} />
                    {item.unit && shown !== "" ? (
                      <span className="anketa-unit">{pick(item.unit, lang)}</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </DocField>
        );
      }
      return (
        <div className="anketa-field anketa-field--pairs">
          <FieldLabel label={field.label} lang={lang} />
          <div className="anketa-pairs">
            {field.items.map((item) => (
              <div className="anketa-pair" key={item.key}>
                <span className="anketa-pair-label">{pick(item.label, lang)}</span>
                <input
                  type="text"
                  value={obj[item.key] || ""}
                  onChange={(e) => handlers.setPair(field.id, item.key, e.target.value)}
                />
                {item.unit ? <span className="anketa-unit">{pick(item.unit, lang)}</span> : null}
              </div>
            ))}
          </div>
        </div>
      );
    }

    default:
      return null;
  }
}

/* ------------------------------------------------------------------ */
/*  Компонент страницы                                                 */
/* ------------------------------------------------------------------ */

function PatientAnketa() {
  const navigate = useNavigate();
  const { patientId, docId } = useParams();

  const [lang, setLang] = useState("ru");
  const [values, setValues] = useState({});
  // Снимок последней сохранённой версии — для отмены правок.
  const [savedValues, setSavedValues] = useState({});
  // По умолчанию анкета открывается в режиме просмотра.
  const [isEditing, setIsEditing] = useState(false);
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handlers = useMemo(
    () => ({
      setVal: (id, val) => setValues((prev) => ({ ...prev, [id]: val })),
      toggleCheck: (id, key) =>
        setValues((prev) => {
          const cur = Array.isArray(prev[id]) ? prev[id] : [];
          return {
            ...prev,
            [id]: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key],
          };
        }),
      setPair: (id, key, val) =>
        setValues((prev) => {
          const cur = prev[id] && typeof prev[id] === "object" ? prev[id] : {};
          return { ...prev, [id]: { ...cur, [key]: val } };
        }),
      setCell: (id, row, col, val) =>
        setValues((prev) => {
          const rows = Array.isArray(prev[id]) ? prev[id].map((r) => ({ ...r })) : [];
          while (rows.length <= row) rows.push({});
          rows[row] = { ...rows[row], [col]: val };
          return { ...prev, [id]: rows };
        }),
    }),
    [],
  );

  const loadDoc = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await GetData({
        module: "patient",
        block: "patient_docs",
        action: "get",
        id_patient: patientId,
        id: docId,
      });

      if (response?.status === "fail" || !response?.result?.id) {
        throw new Error(response?.message || "Документ не найден");
      }

      const doc = response.result;
      if (doc.data && typeof doc.data === "object" && !Array.isArray(doc.data)) {
        setValues(doc.data);
        setSavedValues(doc.data);
      }
      // Открытие документа всегда начинается с просмотра.
      setIsEditing(false);
      setSaved(false);
    } catch (err) {
      setLoadError(err.message || "Не удалось загрузить анкету");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDoc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, docId]);

  const handleEdit = () => {
    setSaveError("");
    setSaved(false);
    setIsEditing(true);
  };

  // Выход без сохранения: правки отбрасываются, возвращаемся к снимку.
  const handleCancel = () => {
    setValues(savedValues);
    setSaveError("");
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    setSaved(false);
    try {
      const response = await GetData({
        module: "patient",
        block: "patient_docs",
        action: "update",
        id_patient: patientId,
        id: docId,
        data: values,
      });
      if (response?.status === "fail") {
        throw new Error(response?.message || "Не удалось сохранить анкету");
      }
      // Фиксируем новую сохранённую версию и возвращаемся в просмотр.
      setSavedValues(values);
      setIsEditing(false);
      setSaved(true);
    } catch (err) {
      // Остаёмся в режиме редактирования, чтобы не потерять введённые данные.
      setSaveError(err.message || "Не удалось сохранить анкету");
    } finally {
      setSaving(false);
    }
  };

  // Печать документа: скрываем всё, кроме контента вкладки (как в остальных
  // документах медкарты), и вызываем системную печать.
  const handlePrint = () => {
    if (typeof window === "undefined") return;
    const body = document.body;
    if (!body) {
      window.print();
      return;
    }

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      body.classList.remove("print-only-profile", "print-profile");
      window.removeEventListener("afterprint", cleanup);
    };

    window.addEventListener("afterprint", cleanup);
    body.classList.add("print-only-profile", "print-profile");
    window.print();
  };

  // Удаление документа анкеты: подтверждение → запрос → запись в журнал → список.
  const handleDelete = async () => {
    const confirmed = window.confirm(
      pick(
        L(
          "Удалить эту анкету? Действие нельзя отменить.",
          "Delete this questionnaire? This cannot be undone."
        ),
        lang
      )
    );
    if (!confirmed) return;

    setDeleting(true);
    setSaveError("");
    try {
      const response = await GetData({
        module: "patient",
        block: "patient_docs",
        action: "delete",
        id_patient: patientId,
        id: docId,
      });

      if (response?.status !== "success") {
        throw new Error(
          response?.message ||
            pick(L("Не удалось удалить анкету", "Failed to delete questionnaire"), lang)
        );
      }

      try {
        await logPatientChange({
          idPatient: patientId,
          idItem: docId,
          text: buildPatientLogText("patient_docs_deleted", {
            details: pick(L("Анкета пациента", "Patient questionnaire"), lang),
          }),
        });
      } catch (_) {
        // Журнал не должен блокировать удаление.
      }

      navigate(`/patient/${patientId}/profile`);
    } catch (err) {
      setSaveError(
        err.message ||
          pick(L("Не удалось удалить анкету", "Failed to delete questionnaire"), lang)
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleGenerateLink = async () => {
    try {
      const response = await GetData({
        module: "anketa",
        block: "link",
        action: "create",
        id_doc: docId,
        id_patient: patientId,
      });

      const path = response?.result?.path;
      if (response?.status === "fail" || !path) {
        throw new Error(response?.message || "Не удалось создать ссылку");
      }

      const fullUrl = `${window.location.origin}${path}`;
      setLink(fullUrl);
      setCopied(false);
      try {
        await navigator.clipboard.writeText(fullUrl);
        setCopied(true);
      } catch (_) {
        // Буфер обмена недоступен — ссылку можно скопировать вручную.
      }
    } catch (err) {
      setLink("");
      setCopied(false);
      // Показываем ошибку в поле статуса.
      setLinkError(err.message || "Не удалось создать ссылку");
    }
  };

  if (loading) {
    return (
      <div className="anketa-page">
        <div style={{ padding: "24px 0", color: "#6b7280" }}>
          {pick(L("Загрузка…", "Loading…"), lang)}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="anketa-page">
        <div className="anketa-link-error">{loadError}</div>
      </div>
    );
  }

  return (
    <div className={`anketa-page${isEditing ? "" : " anketa-page--view"}`}>
      {/* Заголовок и управление */}
      <div className="block-title anketa-header">
        <div className="block-title-text">{pick(L("Анкета пациента", "Patient questionnaire"), lang)}</div>
        <div className="block-title-btn">
          <SegmentedControl
            ariaLabel="Language"
            items={[
              { value: "ru", label: "RU" },
              { value: "en", label: "EN" },
            ]}
            onChange={setLang}
            value={lang}
          />
          {isEditing ? (
            <>
              <Button
                disabled={saving}
                icon="save"
                onClick={handleSave}
                variant="primary"
              >
                {saving
                  ? pick(L("Сохранение…", "Saving…"), lang)
                  : pick(L("Сохранить", "Save"), lang)}
              </Button>
              <Button disabled={saving} onClick={handleCancel} variant="secondary">
                {pick(L("Отмена", "Cancel"), lang)}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleGenerateLink} variant="secondary">
                {pick(L("Сформировать ссылку", "Generate link"), lang)}
              </Button>
              <Button icon="edit" onClick={handleEdit} variant="secondary">
                {pick(L("Редактировать", "Edit"), lang)}
              </Button>
              <IconButton
                icon="print"
                onClick={handlePrint}
                title={pick(L("Печать", "Print"), lang)}
              />
              <IconButton
                disabled={deleting}
                icon="trash"
                onClick={handleDelete}
                title={pick(L("Удалить", "Delete"), lang)}
                variant="danger"
              />
              <IconButton
                icon="back"
                onClick={() => navigate(`/patient/${patientId}/profile`)}
                title={pick(L("Назад к списку", "Back to list"), lang)}
                variant="primary"
              />
            </>
          )}
        </div>
      </div>

      {/* Сформированная ссылка */}
      {link ? (
        <div className="anketa-link-box">
          <span className="anketa-link-label">
            {pick(L("Ссылка на заполнение анкеты", "Questionnaire link"), lang)}:
          </span>
          <input className="anketa-link-input" type="text" readOnly value={link} />
          <span className="anketa-link-status">
            {copied
              ? pick(L("Скопировано", "Copied"), lang)
              : pick(L("Скопируйте вручную", "Copy manually"), lang)}
          </span>
        </div>
      ) : null}

      {/* Ошибка формирования ссылки */}
      {linkError ? (
        <div className="anketa-link-error">
          {linkError}
        </div>
      ) : null}

      {/* Ошибка сохранения (остаёмся в режиме редактирования) */}
      {saveError ? <div className="anketa-link-error">{saveError}</div> : null}

      {/* Статус сохранения */}
      {saved ? (
        <div className="anketa-link-status">
          {pick(L("Анкета сохранена", "Questionnaire saved"), lang)}
        </div>
      ) : null}

      {/* Вводная информация (только в режиме редактирования) */}
      {isEditing ? (
        <div className="anketa-intro">
          <div className="anketa-subtitle">
            {pick(L("Полный опросник пациента", "Complete patient questionnaire"), lang)}
          </div>
          <p className="anketa-intro-text">
            {pick(
              L(
                "Пожалуйста, заполните анкету максимально подробно. Если вопрос к вам не относится, отметьте «нет» или «не применимо». Данные помогают врачу оценить состояние здоровья, риски и выбрать безопасный план обследования и лечения.",
                "Please fill in the questionnaire as fully as possible. If a question does not apply to you, mark 'no' or 'not applicable'. This information helps the doctor assess your health, risks, and choose a safe examination and treatment plan."
              ),
              lang
            )}
          </p>
          <p className="anketa-warning">
            {pick(
              L(
                "Важно: если сейчас есть сильная боль в груди, выраженная одышка, потеря сознания, внезапная слабость или онемение одной стороны тела, нарушение речи, массивное кровотечение, тяжелая аллергическая реакция либо мысли о причинении вреда себе — немедленно обратитесь за экстренной помощью.",
                "Important: if you currently have severe chest pain, marked shortness of breath, loss of consciousness, sudden weakness or numbness on one side of the body, speech difficulty, massive bleeding, a severe allergic reaction, or thoughts of harming yourself — seek emergency care immediately."
              ),
              lang
            )}
          </p>
        </div>
      ) : null}

      {/* Разделы анкеты */}
      <div className={`anketa-sections${isEditing ? "" : " anketa-sections--doc"}`}>
        {ANKETA_SECTIONS.map((section) => (
          <section className="anketa-section" key={section.id}>
            <h3 className="anketa-section-title">
              <span className="anketa-section-num">{section.num}.</span>{" "}
              {pick(section.title, lang)}
            </h3>
            {section.note ? <p className="anketa-section-note">{pick(section.note, lang)}</p> : null}
            <div className="anketa-section-body">
              {section.fields.map((field) => (
                <React.Fragment key={field.id}>
                  {renderField(field, lang, values, handlers, !isEditing)}
                </React.Fragment>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export default PatientAnketa;
